import { useId } from 'react';
import { parseTeachingVisual } from '@/lib/educationalVisual';

const numberLabel = (n: number) => Number(n.toPrecision(4)).toString();
const ink = 'var(--visual-ink, #303034)';
const muted = 'var(--visual-muted, #65656b)';
const paper = 'var(--visual-paper, #f6f6f4)';
const grid = 'var(--visual-grid, #d8d8d8)';
const fills = { none: 'none', light: 'var(--visual-fill-light, #e4e4e2)', dark: 'var(--visual-fill-dark, #9a9a9f)' };
const dashes = [undefined, '9 5', '3 5', '12 4 3 4'];

function ticks([min, max]: [number, number]) {
  const rough = (max - min) / 4;
  const power = 10 ** Math.floor(Math.log10(rough));
  const step = ([1, 2, 5, 10].find(n => n * power >= rough) || 10) * power;
  const values: number[] = [];
  for (let n = Math.ceil(min / step) * step; n <= max + step * 1e-8 && values.length < 10; n += step) values.push(Math.abs(n) < step * 1e-8 ? 0 : n);
  return values;
}

function wrapLabel(label: string, limit = 15) {
  const lines: string[] = [''];
  const words = label.split(' ').flatMap(word => word.length > limit ? word.match(new RegExp(`.{1,${limit}}`, 'g')) || [word] : [word]);
  words.forEach(word => {
    const last = lines.length - 1;
    if ((lines[last] + ' ' + word).trim().length > limit && lines[last]) lines.push(word);
    else lines[last] = `${lines[last]} ${word}`.trim();
  });
  return lines;
}

function diagramPosition(node: { label: string; x: number; y: number }) {
  const height = Math.max(64, wrapLabel(node.label).length * 25 + 24);
  return { x: 100 + node.x * 4, y: Math.max(height / 2 + 12, Math.min(388 - height / 2, 60 + node.y * 2.8)), height };
}

/** Only the shared validated data schema reaches these fixed SVG primitives. */
export default function EducationalVisual({ source }: { source: string }) {
  const id = useId().replace(/[^a-zA-Z0-9_-]/g, '');
  const visual = parseTeachingVisual(source);
  if (!visual) return <p role="status" className="visual-caption">This visual could not be displayed. Ask for a simpler diagram; the explanation above is still available.</p>;
  const arrow = `url(#${id}-arrow)`;
  const graph = visual.kind === 'graph' ? visual : null;
  const X = (n: number) => graph ? 90 + (n - graph.xRange[0]) / (graph.xRange[1] - graph.xRange[0]) * 420 : n;
  const Y = (n: number) => graph ? 320 - (n - graph.yRange[0]) / (graph.yRange[1] - graph.yRange[0]) * 255 : n;
  const zeroX = graph ? X(Math.max(graph.xRange[0], Math.min(0, graph.xRange[1]))) : 0;
  const zeroY = graph ? Y(Math.max(graph.yRange[0], Math.min(0, graph.yRange[1]))) : 0;

  return (
    <figure className="teaching-visual">
      <figcaption>{visual.title}</figcaption>
      <div className="teaching-visual-scroll" role="region" aria-label={`${visual.title} — scroll horizontally if needed`} tabIndex={0}>
        <svg viewBox={graph ? '0 0 600 445' : '0 0 600 400'} role="img" aria-labelledby={`${id}-title ${id}-desc`} fontSize="22" fontFamily="inherit">
          <title id={`${id}-title`}>{visual.title}</title>
          <desc id={`${id}-desc`}>{visual.caption || 'Educational visual'}{graph ? `; horizontal axis: ${graph.xLabel}; vertical axis: ${graph.yLabel}` : ''}</desc>
          <defs>
            <marker id={`${id}-arrow`} markerWidth="8" markerHeight="8" refX="7" refY="4" orient="auto" markerUnits="userSpaceOnUse"><path d="M0,0 L8,4 L0,8" fill={ink} /></marker>
            <clipPath id={`${id}-plot`}><rect x="88" y="60" width="426" height="266" /></clipPath>
          </defs>
          {graph && <>
            {ticks(graph.xRange).map(x => <g key={`x${x}`} fill={muted}>
              <path d={`M${X(x)} 65V320`} stroke={grid} />
              <text x={X(x)} y="350" textAnchor="middle">{numberLabel(x)}</text>
            </g>)}
            {ticks(graph.yRange).map(y => <g key={`y${y}`} fill={muted}>
              <path d={`M90 ${Y(y)}H510`} stroke={grid} />
              <text x="76" y={Y(y) + 7} textAnchor="end">{numberLabel(y)}</text>
            </g>)}
            <line x1="90" y1={zeroY} x2="533" y2={zeroY} stroke={muted} strokeWidth="2" markerEnd={arrow} />
            <line x1={zeroX} y1="320" x2={zeroX} y2="45" stroke={muted} strokeWidth="2" markerEnd={arrow} />
            <text x="300" y="387" fill={ink} textAnchor="middle">{wrapLabel(graph.xLabel, 35).map((line, i) => <tspan key={i} x="300" dy={i ? 26 : 0}>{line}</tspan>)}</text>
            <text x="90" y="27" fill={ink}>{wrapLabel(graph.yLabel, 38).map((line, i) => <tspan key={i} x="90" dy={i ? 26 : 0}>{line}</tspan>)}</text>
            <g clipPath={`url(#${id}-plot)`}>
              {graph.series.map((series, i) => <polyline key={i} points={series.points.map(point => `${X(point[0])},${Y(point[1])}`).join(' ')} fill="none" stroke={ink} strokeWidth="3" strokeDasharray={dashes[i]} />)}
              {graph.vectors.map((vector, i) => <g key={i}>
                <path d={`M${X(vector.from[0])} ${Y(vector.from[1])}H${X(vector.to[0])}V${Y(vector.to[1])}`} fill="none" stroke={muted} strokeDasharray="5 5" />
                <line x1={X(vector.from[0])} y1={Y(vector.from[1])} x2={X(vector.to[0])} y2={Y(vector.to[1])} stroke={ink} strokeWidth="3" markerEnd={arrow} />
                <circle cx={X(vector.to[0])} cy={Y(vector.to[1])} r="3" fill={ink} />
              </g>)}
            </g>
            {graph.vectors.map((vector, i) => <text key={i} x={Math.max(110, Math.min(495, X(vector.to[0])))} y={Math.max(85, Y(vector.to[1]) - 15)} textAnchor={X(vector.to[0]) > 350 ? 'end' : 'start'} fill={ink} stroke={paper} strokeWidth="5" paintOrder="stroke">({vector.to.map(numberLabel).join(', ')})</text>)}
          </>}
          {visual.kind === 'diagram' && <>
            {visual.edges.map((edge, i) => {
              const a = visual.nodes.find(node => node.id === edge.from);
              const b = visual.nodes.find(node => node.id === edge.to);
              if (!a || !b) return null;
              const { x: ax, y: ay } = diagramPosition(a);
              const { x: bx, y: by } = diagramPosition(b);
              const dx = bx - ax, dy = by - ay, distance = Math.hypot(dx, dy) || 1;
              const inset = Math.min(distance / 3, Math.abs(dx) > Math.abs(dy) ? 90 : 48);
              return <g key={i}>
                <line x1={ax + dx / distance * inset} y1={ay + dy / distance * inset} x2={bx - dx / distance * inset} y2={by - dy / distance * inset} stroke={muted} strokeWidth="2" markerEnd={arrow} />
                {edge.label && <text x={(ax + bx) / 2} y={(ay + by) / 2 - 12} textAnchor="middle" fill={ink} stroke={paper} strokeWidth="6" paintOrder="stroke">{wrapLabel(edge.label, 26).map((line, index) => <tspan key={index} x={(ax + bx) / 2} dy={index ? 25 : 0}>{line}</tspan>)}</text>}
              </g>;
            })}
            {visual.nodes.map(node => {
              const lines = wrapLabel(node.label);
              const { x, y, height } = diagramPosition(node);
              return <g key={node.id} transform={`translate(${x},${y})`}>
                <rect x="-92" y={-height / 2} width="184" height={height} rx="12" fill={fills.light} stroke={muted} />
                <text textAnchor="middle" fill={ink}>{lines.map((line, i) => <tspan key={i} x="0" y={7 + (i - (lines.length - 1) / 2) * 25}>{line}</tspan>)}</text>
              </g>;
            })}
          </>}
          {visual.kind === 'illustration' && <g stroke={ink} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            {visual.shapes.map((shape, i) => {
              switch (shape.type) {
                case 'line':
                case 'arrow': return <line key={i} x1={shape.x1} y1={shape.y1} x2={shape.x2} y2={shape.y2} strokeDasharray={shape.dashed ? '8 6' : undefined} markerEnd={shape.type === 'arrow' ? arrow : undefined} />;
                case 'rect': return <rect key={i} x={shape.x} y={shape.y} width={shape.width} height={shape.height} fill={fills[shape.fill || 'none']} />;
                case 'ellipse': return <ellipse key={i} cx={shape.cx} cy={shape.cy} rx={shape.rx} ry={shape.ry} fill={fills[shape.fill || 'none']} />;
                case 'polygon': return <polygon key={i} points={shape.points.map(point => point.join(',')).join(' ')} fill={fills[shape.fill || 'none']} />;
                case 'text': {
                  const lines = wrapLabel(shape.text, 34);
                  const anchor = shape.anchor || 'middle';
                  const width = Math.max(...lines.map(line => line.length)) * 12;
                  const leftInset = anchor === 'end' ? width : anchor === 'middle' ? width / 2 : 0;
                  const rightInset = anchor === 'start' ? width : anchor === 'middle' ? width / 2 : 0;
                  const x = Math.max(14 + leftInset, Math.min(586 - rightInset, shape.x));
                  const y = Math.max(28, Math.min(382 - (lines.length - 1) * 26, shape.y));
                  return <text key={i} x={x} y={y} fill={ink} stroke={paper} strokeWidth="5" paintOrder="stroke" fontSize="22" textAnchor={anchor}>{lines.map((line, lineIndex) => <tspan key={lineIndex} x={x} dy={lineIndex === 0 ? 0 : 26}>{line}</tspan>)}</text>;
                }
              }
            })}
          </g>}
        </svg>
      </div>
      {graph && <ul className="teaching-visual-key" aria-label="Graph key">
        {graph.series.map((series, i) => <li key={i}><svg aria-hidden="true" viewBox="0 0 28 10" style={{ width: 28, minWidth: 28, height: 10 }}><line x1="0" y1="5" x2="28" y2="5" stroke={ink} strokeWidth="2" strokeDasharray={dashes[i]} /></svg>{series.label}</li>)}
        {graph.vectors.map((vector, i) => <li key={`vector${i}`}>{vector.label}: ({vector.from.map(numberLabel).join(', ')}) → ({vector.to.map(numberLabel).join(', ')})</li>)}
      </ul>}
      {visual.caption && <p className="visual-caption">{visual.caption}</p>}
    </figure>
  );
}
